import React from 'react';
import { Box, Container, Grid, Typography, Card, CardContent, CardMedia, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { motion } from 'framer-motion';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import ReceiptIcon from '@mui/icons-material/Receipt';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import BuildIcon from '@mui/icons-material/Build';
import AssessmentIcon from '@mui/icons-material/Assessment';
import PeopleIcon from '@mui/icons-material/People';

const featuresList = [
  {
    title: 'Recepción de Vehículos',
    description: 'Registra vehículos en segundos consultando por patente y documentando su estado con fotos.',
    icon: DirectionsCarIcon,
    color: '#1976d2'
  },
  {
    title: 'Fichas de Servicio',
    description: 'Crea fichas de servicio con diagnósticos, presupuestos y seguimiento de reparaciones.',
    icon: ReceiptIcon,
    color: '#f50057'
  },
  {
    title: 'Agenda y Calendario',
    description: 'Gestiona citas con clientes, asigna trabajos a mecánicos y visualiza la ocupación del taller.',
    icon: CalendarMonthIcon,
    color: '#4caf50'
  },
  {
    title: 'Gestión de Repuestos',
    description: 'Maneja inventario de repuestos, registra compras y controla el stock de insumos.',
    icon: BuildIcon,
    color: '#ff9800'
  },
  {
    title: 'Reportes y Estadísticas',
    description: 'Analiza el rendimiento con reportes personalizados sobre ingresos, servicios y productividad.',
    icon: AssessmentIcon,
    color: '#9c27b0'
  },
  {
    title: 'Gestión de Clientes',
    description: 'Mantén registro de clientes, historial de servicios y programa recordatorios de mantenimiento.',
    icon: PeopleIcon,
    color: '#00bcd4'
  }
];

const Features = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const container = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.3
      }
    }
  };

  const item = {
    hidden: { opacity: 0, y: 20 },
    show: { opacity: 1, y: 0, transition: { duration: 0.5 } }
  };

  return (
    <Box id="funcionalidades" sx={{ py: 8, bgcolor: 'background.default' }}>
      <Container maxWidth="lg">
        <Box sx={{ textAlign: 'center', mb: 8 }}>
          <Typography
            component={motion.div}
            initial={{ opacity: 0, y: -20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            variant="overline"
            color="primary"
            sx={{ fontWeight: 600, letterSpacing: 1.2 }}
          >
            FUNCIONALIDADES
          </Typography>
          <Typography
            component={motion.div}
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            viewport={{ once: true }}
            variant="h3"
            sx={{ mt: 1, mb: 2, fontWeight: 700 }}
          >
            Todo lo que necesitas para tu taller
          </Typography>
          <Typography
            component={motion.div}
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            viewport={{ once: true }}
            variant="h6"
            color="text.secondary"
            sx={{ maxWidth: '800px', mx: 'auto', fontWeight: 400 }}
          >
            MekaSys ofrece todas las herramientas para optimizar la gestión de tu taller mecánico,
            desde la recepción del vehículo hasta la entrega final.
          </Typography>
        </Box>

        <Grid
          container
          spacing={4}
          component={motion.div}
          variants={container}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true }}
        >
          {featuresList.map((feature, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <motion.div variants={item}>
                <Card 
                  elevation={1}
                  sx={{ 
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    transition: 'transform 0.3s, box-shadow 0.3s',
                    '&:hover': {
                      transform: 'translateY(-5px)',
                      boxShadow: '0 12px 20px rgba(0, 0, 0, 0.08)'
                    },
                    bgcolor: 'background.paper'
                  }}
                >
                  <CardContent sx={{ flexGrow: 1, p: 3 }}>
                    <Box 
                      sx={{ 
                        bgcolor: `${feature.color}15`, 
                        color: feature.color,
                        width: 60,
                        height: 60,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderRadius: '12px',
                        mb: 2
                      }}
                    >
                      <feature.icon sx={{ fontSize: 30 }} />
                    </Box>
                    <Typography variant="h5" component="h3" gutterBottom fontWeight={600}>
                      {feature.title}
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                      {feature.description}
                    </Typography>
                  </CardContent>
                </Card>
              </motion.div>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
};

export default Features; 